// Step navigation logic for onboarding flow
// Pure functions for parsing and building step URLs
// Part of ViewModel layer - provides navigation logic

// ADT for onboarding step
export type OnboardingStep =
  | { type: 'question'; index: number }
  | { type: 'result' }
  | { type: 'strategy' };

const STEP_PARAM = 'step';
const STRATEGY_STEP = 'strategy';
const RESULT_STEP = 'result';

// Parse step from URL query string
export const parseStep = (totalQuestions: number): OnboardingStep => {
  const params = new URLSearchParams(window.location.search);
  const stepValue = params.get(STEP_PARAM);

  // Strategy step
  if (stepValue === STRATEGY_STEP) {
    return { type: 'strategy' };
  }

  // Result step
  if (stepValue === RESULT_STEP) {
    return { type: 'result' };
  }

  // Question step - parse as number
  const stepNum = stepValue ? parseInt(stepValue, 10) : 1;

  // Validate step is within bounds (1 to totalQuestions)
  if (isNaN(stepNum) || stepNum < 1) {
    return { type: 'question', index: 0 };
  }

  if (stepNum > totalQuestions) {
    return { type: 'strategy' };
  }

  // Convert 1-indexed URL param to 0-indexed
  return { type: 'question', index: stepNum - 1 };
};

// Get base path from current URL (preserves admin context)
const getBasePath = (): string => {
  // Get pathname without query string
  const pathname = window.location.pathname;
  return pathname;
};

// Build URL with step param, preserving current route context
export const buildStepUrl = (step: OnboardingStep): string => {
  let stepValue: string;
  if (step.type === 'strategy') {
    stepValue = STRATEGY_STEP;
  } else if (step.type === 'result') {
    stepValue = RESULT_STEP;
  } else {
    stepValue = String(step.index + 1);
  }
  const basePath = getBasePath();
  return `${basePath}?${STEP_PARAM}=${stepValue}`;
};

// Helper to get current question index from step
export const getQuestionIndex = (step: OnboardingStep): number | null => {
  return step.type === 'question' ? step.index : null;
};

// Helper to check if step is strategy selection
export const isStrategyStep = (step: OnboardingStep): boolean => {
  return step.type === 'strategy';
};

// Helper to check if step is result screen
export const isResultStep = (step: OnboardingStep): boolean => {
  return step.type === 'result';
};

// Calculate next step
export const getNextStep = (
  currentStep: OnboardingStep,
  totalQuestions: number,
): OnboardingStep | null => {
  if (currentStep.type === 'question') {
    const nextIndex = currentStep.index + 1;
    if (nextIndex >= totalQuestions) {
      return { type: 'result' };
    }
    return { type: 'question', index: nextIndex };
  }

  if (currentStep.type === 'result') {
    return { type: 'strategy' };
  }

  // Already on strategy step
  return null;
};

// Calculate previous step
export const getPreviousStep = (
  currentStep: OnboardingStep,
  totalQuestions: number,
): OnboardingStep | null => {
  if (currentStep.type === 'strategy') {
    // Go back to result
    return { type: 'result' };
  }

  if (currentStep.type === 'result') {
    // Go back to last question
    return { type: 'question', index: totalQuestions - 1 };
  }

  if (currentStep.type === 'question' && currentStep.index > 0) {
    return { type: 'question', index: currentStep.index - 1 };
  }

  // On first question
  return null;
};
